import { Box, Card, CardHeader, Stack, Typography } from "@mui/material";
import React, { useContext, useState } from "react";
import CustomLoadingButton from "../../../components/CustomComponents.tsx/CustomLoadingButton";
import { useNavigate } from "react-router-dom";
import { PATH_KYC } from "../../../routes/paths";
import LoadingScreen from "../../../components/loading-screen";
import fetcher from "../../../api/fetcher";
import { END_POINTS } from "../../../api/EndPoints";
import { BusinessDetailsContext } from "../../../layouts/Kyc/KYCLayout";
import { useAuthContext } from "../../../auth/useAuthContext";
import { showToast } from "../../../utils/Toast";

type props = {
  aadhaarData: {
    aadharNumber: string;
    nameInAadhaar: string;
    aadharDOB: string;
    aadharGender: string;
    distInAadhar: string;
    aadharPincode: string;
    aadharState: string;
    cityInAadhar: string;
    addressInAadhar: string;
    aadharCO: string;
    aadharCountry: string;
    aadharHouse: string; // not present in your data
    aadharPO: string;
    aadharStreet: string; // not present
    aadharSubDistrict: string; // not present
    aadharPhoto: string;
  };
  panData: {
    name: string;
    dob: string;
    pan: string;
  };
  setSession: (session: string) => void;
  isAadhaarDetailFetching: boolean;
  isPanDetailsFetching: boolean;
};

const RowStack = ({ label, value }: { label: string; value: string }) => {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="subtitle1">{label}</Typography>
      <Typography variant="body1">{value}</Typography>
    </Stack>
  );
};

function AadhaarAndPanDetailsPage({
  aadhaarData,
  panData,
  setSession,
  isAadhaarDetailFetching,
  isPanDetailsFetching,
}: props) {
  const { initialize } = useAuthContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getBusinessDetail } = useContext(BusinessDetailsContext);
  const navigate = useNavigate();

  const onSubmit = async () => {
    try {
      setIsSubmitting(true);
      if (!(aadhaarData.nameInAadhaar && panData.name)) {
        navigate(PATH_KYC.directorAndPromoter, { replace: true });
        setSession("");
        return;
      }
      const body = {
        ...aadhaarData,
        pan: panData.pan,
      };

      console.log(body);
      const response = await fetcher.post(
        END_POINTS.KYC.AADHAAR.SAVE_DIGILOCKER_DETAILS,
        body
      );
      if (!response.data.success) {
        showToast.error(response.data.message || "Failed to save details");
      }

      // getBusinessDetail();
      await initialize();
      setSession("");
      navigate(PATH_KYC.directorAndPromoter, { replace: true });
    } catch (error) {
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack flexDirection={"column"} width="100%" gap={2}>
      <Card
        sx={{
          p: 3,
          borderRadius: 1,
          maxWidth: 720,
          border: "1px solid #dadada",
        }}
      >
        <CardHeader
          title="Aadhaar Details"
          sx={{ p: 0, textAlign: "center", mb: 2 }}
        />

        {isAadhaarDetailFetching && (
          <Box sx={{ mt: 15 }}>
            <LoadingScreen />
          </Box>
        )}

        {aadhaarData?.nameInAadhaar && (
          <Stack flexDirection={"row"} justifyContent={"space-between"} gap={2}>
            <Box sx={{ flex: 1 }}>
              <RowStack label="Name" value={aadhaarData.nameInAadhaar} />
              <RowStack label="Date of Birth" value={aadhaarData.aadharDOB} />
              <RowStack label="Gender" value={aadhaarData.aadharGender} />
              <br />
              <RowStack label="Address" value={aadhaarData.addressInAadhar} />
              <RowStack label="State" value={aadhaarData.aadharState} />
              <RowStack label="District" value={aadhaarData.distInAadhar} />
              <RowStack label="Pincode" value={aadhaarData.aadharPincode} />
            </Box>
            <Box>
              <img
                src={"data:image/jpeg;base64," + aadhaarData.aadharPhoto}
                alt=""
                height={140}
                width={140}
                style={{
                  border: "1px solid #dadada",
                  borderRadius: 12,
                }}
              />
            </Box>
          </Stack>
        )}

        {!aadhaarData?.nameInAadhaar && !isAadhaarDetailFetching && (
          <Typography variant="h4" color="error.main" textAlign={"center"}>
            No Data Found
          </Typography>
        )}
      </Card>
      <Card
        sx={{
          p: 3,
          borderRadius: 1,
          maxWidth: 720,
          border: "1px solid #dadada",
        }}
      >
        <CardHeader
          title="Pan Details"
          sx={{ p: 0, textAlign: "center", mb: 2 }}
        />

        {isPanDetailsFetching && (
          <Box sx={{ mt: 15 }}>
            <LoadingScreen />
          </Box>
        )}

        {panData?.name && (
          <Box>
            <RowStack label="Pan" value={panData.pan} />
            <RowStack label="Name" value={panData.name} />
            <RowStack label="Date of Birth" value={panData.dob} />
          </Box>
        )}

        {!panData?.name && !isPanDetailsFetching && (
          <Typography variant="h4" color="error.main" textAlign={"center"}>
            No Data Found
          </Typography>
        )}
      </Card>
      {(aadhaarData.nameInAadhaar ||
        panData.name ||
        (!isAadhaarDetailFetching && !isPanDetailsFetching)) && (
        <CustomLoadingButton
          variant="contained"
          color="primary"
          onClick={onSubmit}
          size="large"
          disabled={isAadhaarDetailFetching || isPanDetailsFetching}
          loading={isSubmitting}
          sx={{ width: "fit-content" }}
        >
          {panData?.name && aadhaarData?.nameInAadhaar ? "Continue" : "Retry"}
        </CustomLoadingButton>
      )}
    </Stack>
  );
}

export default React.memo(AadhaarAndPanDetailsPage);
